import { z } from 'zod';
import { CursorQuery } from './common/pagination.js';
import { UserSummary } from './profile.js';
export const FamilyRelation = z.enum([
    'spouse',
    'husband',
    'wife',
    'father',
    'mother',
    'son',
    'daughter',
    'parent',
    'child',
    'brother',
    'sister',
    'sibling',
    'grandparent',
    'grandchild',
    'cousin',
    'aunt_uncle',
    'niece_nephew',
    'sibling_in_law',
    'brother_in_law',
    'sister_in_law',
    'parent_in_law',
    'father_in_law',
    'mother_in_law',
    'child_in_law',
    'son_in_law',
    'daughter_in_law',
]);
export const FAMILY_RELATION_LABELS = {
    spouse: 'Spouse',
    husband: 'Husband',
    wife: 'Wife',
    father: 'Father',
    mother: 'Mother',
    son: 'Son',
    daughter: 'Daughter',
    parent: 'Parent',
    child: 'Child',
    brother: 'Brother',
    sister: 'Sister',
    sibling: 'Sibling',
    grandparent: 'Grandparent',
    grandchild: 'Grandchild',
    cousin: 'Cousin',
    aunt_uncle: 'Aunt / Uncle',
    niece_nephew: 'Niece / Nephew',
    sibling_in_law: 'Sibling-in-law',
    brother_in_law: 'Brother-in-law',
    sister_in_law: 'Sister-in-law',
    parent_in_law: 'Parent-in-law',
    father_in_law: 'Father-in-law',
    mother_in_law: 'Mother-in-law',
    child_in_law: 'Child-in-law',
    son_in_law: 'Son-in-law',
    daughter_in_law: 'Daughter-in-law',
};
export const FAMILY_REQUEST_RELATIONS = [
    'spouse',
    'husband',
    'wife',
    'father',
    'mother',
    'son',
    'daughter',
    'parent',
    'child',
    'brother',
    'sister',
    'grandparent',
    'grandchild',
    'cousin',
    'aunt_uncle',
];
/** Never infer the requester's gender from a name or photograph. */
export function inverseFamilyRelation(relation) {
    return {
        spouse: 'spouse',
        husband: 'spouse',
        wife: 'spouse',
        father: 'child',
        mother: 'child',
        son: 'parent',
        daughter: 'parent',
        parent: 'child',
        child: 'parent',
        brother: 'sibling',
        sister: 'sibling',
        sibling: 'sibling',
        grandparent: 'grandchild',
        grandchild: 'grandparent',
        cousin: 'cousin',
        aunt_uncle: 'niece_nephew',
        niece_nephew: 'aunt_uncle',
        sibling_in_law: 'sibling_in_law',
        brother_in_law: 'sibling_in_law',
        sister_in_law: 'sibling_in_law',
        parent_in_law: 'child_in_law',
        father_in_law: 'child_in_law',
        mother_in_law: 'child_in_law',
        child_in_law: 'parent_in_law',
        son_in_law: 'parent_in_law',
        daughter_in_law: 'parent_in_law',
    }[relation];
}
/** Gender comes only from the member's own saved declaration, never names or photos. */
export function reciprocalFamilyRelation(relation, gender) {
    const inverse = inverseFamilyRelation(relation);
    if (inverse === 'sibling_in_law')
        return gender === 'male' ? 'brother_in_law' : gender === 'female' ? 'sister_in_law' : inverse;
    if (inverse === 'parent_in_law')
        return gender === 'male' ? 'father_in_law' : gender === 'female' ? 'mother_in_law' : inverse;
    if (inverse === 'child_in_law')
        return gender === 'male' ? 'son_in_law' : gender === 'female' ? 'daughter_in_law' : inverse;
    if (inverse === 'spouse')
        return gender === 'male' ? 'husband' : gender === 'female' ? 'wife' : inverse;
    if (inverse === 'sibling')
        return gender === 'male' ? 'brother' : gender === 'female' ? 'sister' : inverse;
    if (inverse === 'parent')
        return gender === 'male' ? 'father' : gender === 'female' ? 'mother' : inverse;
    if (inverse === 'child')
        return gender === 'male' ? 'son' : gender === 'female' ? 'daughter' : inverse;
    return inverse;
}
export const FamilySide = z.enum(['maternal', 'paternal', 'unspecified']);
export const FamilyRequestState = z.enum([
    'pending',
    'accepted',
    'declined',
    'cancelled',
    'removed',
]);
export const FamilyGroupAccessView = z.object({
    circleId: z.string(),
    name: z.string(),
    viewerState: z.enum(['active', 'pending']),
    relativeState: z.enum(['active', 'pending', 'unavailable']),
});
export const FamilyRequestView = z.object({
    id: z.string(),
    requesterId: z.string(),
    recipientId: z.string(),
    requester: UserSummary.nullable(),
    recipient: UserSummary.nullable(),
    relation: FamilyRelation,
    inverseRelation: FamilyRelation,
    state: FamilyRequestState,
    side: FamilySide,
    version: z.number().int().nonnegative(),
    correctionOfVersion: z.number().int().nonnegative().optional(),
    circleId: z.string().optional(),
    familyName: z.string().optional(),
    groupApprovalRequired: z.boolean().optional(),
    groupAccess: z.array(FamilyGroupAccessView).optional(),
    mutualFamily: z.array(UserSummary).max(3).optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    undoUntil: z.coerce.date().nullable(),
});
export const FamilyMemberView = z.object({
    relationshipId: z.string(),
    person: UserSummary,
    relation: FamilyRelation,
    side: FamilySide,
    since: z.coerce.date(),
    version: z.number().int().nonnegative(),
});
export const FamilySummary = z.object({
    memberCount: z.number().int().nonnegative(),
    incomingCount: z.number().int().nonnegative(),
    outgoingCount: z.number().int().nonnegative(),
    members: z.array(FamilyMemberView),
    incoming: z.array(FamilyRequestView),
    capabilities: z.object({
        relationships: z.literal(true),
        familyGroups: z.boolean(),
        milestones: z.boolean(),
        awards: z.boolean(),
    }),
});
export const CreateFamilyRequestInput = z.object({
    /** Explicitly start the sender's own side instead of reusing a family they joined. */
    startOwnFamily: z.boolean().optional(),
    recipientId: z.string().trim().min(1).max(128),
    circleId: z.string().trim().min(1).max(128).optional(),
    relation: FamilyRelation,
    side: FamilySide.default('unspecified'),
});
export const FamilyRequestActionInput = z.object({
    expectedVersion: z.number().int().nonnegative(),
    side: FamilySide.optional(),
    /** The recipient's existing family to invite the sender into on acceptance. */
    circleId: z.string().trim().min(1).max(128).optional(),
});
export const CorrectFamilyRequestInput = FamilyRequestActionInput.extend({
    /** The proposed recipient's relation to the member making the correction. */
    relation: FamilyRelation,
});
export const FamilyListQuery = CursorQuery.extend({ q: z.string().trim().max(100).optional() });
export const FamilyRequestsQuery = CursorQuery.extend({
    direction: z.enum(['incoming', 'outgoing']).default('incoming'),
});
export const CreateFamilyInviteInput = CreateFamilyRequestInput.omit({ recipientId: true });
export const FamilyInviteTokenInput = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{43}$/) });
export const FamilyInviteView = z.object({
    id: z.string(),
    relation: FamilyRelation,
    state: z.enum(['open', 'claimed', 'revoked']),
    expiresAt: z.coerce.date(),
    createdAt: z.coerce.date(),
    requestId: z.string().nullable(),
});
export const CreatedFamilyInviteView = FamilyInviteView.extend({ token: z.string() });
export const FamilyInvitePreview = z.object({
    inviter: UserSummary,
    relation: FamilyRelation,
    inverseRelation: FamilyRelation,
    expiresAt: z.coerce.date(),
});
export const FamilyKinship = z.enum([...FamilyRelation.options, 'relative']);
export const FAMILY_KINSHIP_LABELS = {
    ...FAMILY_RELATION_LABELS,
    relative: 'Relative',
    sibling_in_law: 'Sibling-in-law',
    brother_in_law: 'Brother-in-law',
    sister_in_law: 'Sister-in-law',
    parent_in_law: 'Parent-in-law',
    father_in_law: 'Father-in-law',
    mother_in_law: 'Mother-in-law',
    child_in_law: 'Child-in-law',
    son_in_law: 'Son-in-law',
    daughter_in_law: 'Daughter-in-law',
};
export const FamilySuggestionView = z.object({
    person: UserSummary,
    through: UserSummary,
    relation: FamilyKinship,
    description: z.string(),
    suggestedRelation: FamilyRelation.nullable(),
});
export const FamilyCircleView = z.object({
    id: z.string(),
    kind: z.literal('family'),
    name: z.string(),
    ownerId: z.string(),
    myRole: z.enum(['owner', 'admin', 'member']),
    myState: z.enum(['active', 'pending']),
    memberCount: z.number().int().nonnegative().nullable(),
    pendingCount: z.number().int().nonnegative(),
    autoAdd: z.boolean(),
    additionMode: z.enum(['anyone', 'approval']),
    version: z.number().int().nonnegative(),
    chatAvailable: z.boolean(),
});
export const FamilyCircleMemberView = z.object({
    user: UserSummary,
    role: z.enum(['owner', 'admin', 'member']),
    state: z.enum(['active', 'pending']),
    relation: FamilyKinship,
    derived: z.boolean(),
    relationDescription: z.string().optional(),
    joinedAt: z.coerce.date(),
});
export const FamilyCircleSettingsInput = z.object({
    expectedVersion: z.number().int().nonnegative(),
    name: z.string().trim().min(1).max(80).optional(),
    autoAdd: z.boolean().optional(),
    additionMode: z.enum(['anyone', 'approval']).optional(),
});
export const FamilyCircleMembersQuery = FamilyListQuery.extend({
    state: z.enum(['active', 'pending']).default('active'),
});
export const FamilyCircleRoleInput = z.object({
    expectedVersion: z.number().int().nonnegative(),
    role: z.enum(['admin', 'member']),
});
const FamilyTimezone = z
    .string()
    .min(1)
    .max(100)
    .refine((zone) => {
    try {
        new Intl.DateTimeFormat('en', { timeZone: zone }).format(0);
        return true;
    }
    catch {
        return false;
    }
}, 'Choose a valid time zone');
export const FamilyEventKind = z.enum(['event', 'birthday', 'anniversary']);
export const FAMILY_EVENT_LABELS = {
    event: 'Family event',
    birthday: 'Birthday',
    anniversary: 'Anniversary',
};
const FamilyEventFields = z.object({
    kind: FamilyEventKind,
    title: z.string().trim().min(1).max(120),
    description: z.string().trim().max(4000).default(''),
    startsAt: z.coerce.date(),
    endsAt: z.coerce.date(),
    timezone: FamilyTimezone,
});
const orderedEvent = (event) => event.endsAt > event.startsAt &&
    event.endsAt.getTime() - event.startsAt.getTime() <= 31 * 86400000;
export const CreateFamilyEventInput = FamilyEventFields.extend({
    idempotencyKey: z.string().uuid(),
}).refine(orderedEvent, { message: 'End must follow start, within 31 days', path: ['endsAt'] });
export const UpdateFamilyEventInput = FamilyEventFields.extend({
    expectedVersion: z.number().int().nonnegative(),
}).refine(orderedEvent, { message: 'End must follow start, within 31 days', path: ['endsAt'] });
export const FamilyCalendarQuery = CursorQuery.extend({
    from: z.coerce.date(),
    to: z.coerce.date(),
}).refine((q) => q.to > q.from && q.to.getTime() - q.from.getTime() <= 366 * 86400000, {
    message: 'Choose a range of at most one year',
    path: ['to'],
});
export const FamilyEventView = FamilyEventFields.extend({
    id: z.string(),
    circleId: z.string(),
    authorId: z.string(),
    author: UserSummary.nullable(),
    version: z.number().int().nonnegative(),
    canEdit: z.boolean(),
    canDelete: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    myReminderMinutes: z.union([z.literal(0), z.literal(60), z.literal(1440)]).nullable(),
});
export const FamilyReminderInput = z.object({
    minutes: z.union([z.literal(0), z.literal(60), z.literal(1440)]).nullable(),
});
export const FamilyReminderView = z.object({
    minutes: FamilyReminderInput.shape.minutes,
    dueAt: z.coerce.date().nullable(),
});
export const FamilyDeleteInput = z.object({ expectedVersion: z.number().int().nonnegative() });
export const FamilyDeletedView = z.object({ id: z.string(), deleted: z.literal(true) });
export const CreateFamilyStoryInput = z.object({
    body: z.string().trim().min(1).max(5000),
    idempotencyKey: z.string().uuid(),
});
export const UpdateFamilyStoryInput = z.object({
    body: z.string().trim().min(1).max(5000),
    expectedVersion: z.number().int().nonnegative(),
});
export const FamilyStoryView = z.object({
    id: z.string(),
    circleId: z.string(),
    authorId: z.string(),
    author: UserSummary.nullable(),
    body: z.string(),
    version: z.number().int().nonnegative(),
    canEdit: z.boolean(),
    canDelete: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const FamilyCongratulationsInput = z.object({
    message: z.string().trim().max(500).default(''),
    // null means first creation. Tombstones keep their version for safe undo/re-entry.
    expectedVersion: z.number().int().nonnegative().nullable(),
});
export const FamilyCongratulationsView = z.object({
    id: z.string(),
    eventId: z.string(),
    author: UserSummary.nullable(),
    message: z.string(),
    version: z.number().int().nonnegative(),
    canDelete: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const FamilyCelebrationSummary = z.object({
    eventId: z.string(),
    congratulationsCount: z.number().int().nonnegative(),
    mine: FamilyCongratulationsView.nullable(),
    myVersion: z.number().int().nonnegative().nullable(),
});
export const SendFamilyChatMessageInput = z.object({
    clientId: z.string().trim().min(1).max(128),
    body: z.string().trim().min(1).max(4000),
});
export const FamilyChatMessageView = z.object({
    id: z.string(),
    circleId: z.string(),
    threadId: z.string(),
    senderId: z.string(),
    sender: UserSummary.nullable(),
    clientId: z.string(),
    body: z.string(),
    kind: z.enum(['message', 'member_joined']).optional(),
    createdAt: z.coerce.date(),
});
export const FamilyChatView = z.object({
    id: z.string(),
    circleId: z.string(),
    kind: z.enum(['group', 'private']),
    peer: UserSummary.nullable(),
    lastMessage: FamilyChatMessageView.nullable(),
    unreadCount: z.number().int().nonnegative(),
    peerReadAt: z.coerce.date().nullable(),
    peerReadMessageId: z.string().nullable(),
});
export const OpenFamilyPrivateChatInput = z.object({ userId: z.string().trim().min(1).max(128) });
export const ReadFamilyChatInput = z.object({ throughMessageId: z.string().uuid() });
export const FamilyChatsQuery = CursorQuery.extend({ q: z.string().trim().max(100).optional() });
//# sourceMappingURL=family.js.map